import { Component, inject, OnInit } from '@angular/core';
import { BaseComponent } from '@app/finxone-web-frontend/app/Modules/shared/widgets/base/base.component';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { DownloadDocumentService } from '@app/finxone-web-frontend/app/lib/services/download-document/download-document.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-range-calendar-widget',
  templateUrl: './range-calendar-widget.component.html',
})
export class RangeCalendarWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public maxDate: Date;
  public minDate: Date;
  public startDate: string | null;
  public endDate: string | null;
  public resetButtonContent: string;
  public resetButtonStyle: baseCssConfigStyle;
  public selectedDateRange: any = [];
  public selectionMode: 'single' | 'range' = 'range';
  private widgetType: 'old' | 'new' = 'old';
  private formKey: string;
  public textColor: baseCssConfigStyle;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public renderedContent = '';

  private store = inject(Store);
  private configService = inject(ConfigService);
  public dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  public pageConfig = inject(PageConfigurationProvider);
  private countriesCurrenciesService = inject(CountriesCurrenciesService);
  private iconCacheService = inject(IconCacheService);
  private accountService = inject(AccountService);
  private downloadDocumentService = inject(DownloadDocumentService);

  ngOnInit(): void {
    if (this.dataIn) {
      this.attri = this.dataIn.attri ?? null;
      this.onLoadWidgetConfiguration();
      this.onLoadWidgetContent();
    }
    const d = new Date();
    this.minDate = d;

    if (this.dataIn.widgetConfig?.nestedWidgets?.['resetButton']) {
      this.resetButtonStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'resetButton'),
        buttonNestedWidgetConfigStyles,
      );
    } else {
      this.resetButtonStyle = {};
    }

    if (this.dataIn.widgetConfig.attributes.widgetProperties?.['widgetType'] === 'new') {
      this.selectionMode =
        this.dataIn.widgetConfig.attributes.widgetProperties?.['selectionMode'] ?? this.selectionMode;
      this.widgetType =
        this.dataIn.widgetConfig.attributes.widgetProperties?.['widgetType'] ?? this.widgetType;
      this.formKey = this.dataIn.widgetConfig.attributes.widgetProperties?.['formKey'];

      this.onSetFormStateDate();
    }
    this.getGlobalStyling();
  }

  private onLoadWidgetContent(): void {
    this.resetButtonContent =
      this.dataIn.widgetConfig?.nestedWidgets?.['resetButton']?.widgetProperties?.textContent ??
      'Reset changes';
    const template = Handlebars.compile(this.resetButtonContent);
    buildHandleBarModel(
      this.store,
      this.resetButtonContent,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['resetButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['resetButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['resetButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['resetButton'].widgetProperties?.['globalStyling'] ?? false;
    }
  }

  private onSetFormStateDate() {
    const data = this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formKey));
    if (data?.formData?.['startDate']) {
      this.startDate = dayjs(data?.formData?.['startDate']).toDate().toString();
      this.selectedDateRange = dayjs(data?.formData?.['startDate']).toDate();
    }
    if (this.selectionMode !== 'single') {
      if (data?.formData?.['endDate']) {
        this.endDate = dayjs(data?.formData?.['endDate']).toDate().toString();
        this.selectedDateRange = [this.selectedDateRange, dayjs(data?.formData?.['endDate']).toDate()];
      } else {
        this.selectedDateRange = [];
      }
    }
  }

  onResetChange() {
    this.startDate = null;
    this.endDate = null;
    this.selectedDateRange = [];
    this.downloadDocumentService.setDateRange(null, null);
  }

  private onLoadWidgetConfiguration(): void {
    this.onSetDefaultBorder();
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
    ]);

    this.textColor = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);
  }

  /*
    Earlier, default border was set in the css and override config style therefore setting up here
   */
  private onSetDefaultBorder() {
    if (!this.attri.css_config?.['borderWidth']) {
      this.attri.css_config['borderWidth'] = 1;
      this.attri.css_config['borderColor'] = 'var(--color-border-color)';
    }
  }

  public dateChange(newDate: (string | null)[]) {
    this.startDate = newDate[0];
    this.endDate = newDate[1];
    this.storeDateIntoFormActionState();
  }

  private storeDateIntoFormActionState() {
    let newStartDate = '';
    let newEndDate = '';

    if (this.startDate) {
      newStartDate = this.formatDate(this.startDate);
      this.startDate = null;
    }
    if (this.endDate) {
      newEndDate = this.formatDate(this.endDate);
      this.endDate = null;
    }

    this.downloadDocumentService.setDateRange(newStartDate, newEndDate);
    this.store.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: {
            startDate: newStartDate,
            endDate: newEndDate,
          },
        },
        this.formKey,
      ),
    );
  }

  private formatDate(date: any): string {
    return dayjs(date).format('YYYY-MM-DD');
  }
}
